import React, { useState, useEffect, useMemo } from 'react';
import {
  Container,
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Breadcrumbs,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Divider,
  Autocomplete
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  NavigateNext as NavigateNextIcon,
  Home as HomeIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format } from 'date-fns';
import { SelectChangeEvent } from '@mui/material/Select';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { UserRole } from '../../types';
import { useAuth } from '../../context/AuthContext';
import { memberProfileAPI, membershipTierAPI, userAPI, oneTimeFeeAPI, discountAPI, memberGroupAPI } from '../../services/api';
import { MemberProfile, MembershipStatus, GroupRole, CreateMemberProfileRequest } from '../../types/memberProfile';
import { MembershipTier, User, OneTimeFee, Discount, MemberGroup } from '../../types';
import { formatDate } from '../../utils/dateUtils';
import AdminAccessControl from '../common/AdminAccessControl';

interface MemberProfileFormData {
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  mobile?: string;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  country?: string;
  dateOfBirth?: Date | null;
  membershipTierId: string;
  startDate?: Date | null;
  endDate?: Date | null;
  monthlyPrice?: number;
  autoRenew: boolean;
  membershipNumber?: string;
  memberGroupId?: number;
  groupRole?: GroupRole; // Optional - only set when member is in a group
  notes?: string;
  selectedFees?: string[];
  selectedDiscounts?: string[];
}

const initialFormData: MemberProfileFormData = {
  userId: '',
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  mobile: '',
  address: '',
  city: '',
  state: '',
  zipCode: '',
  country: '',
  dateOfBirth: null,
  membershipTierId: '',
  startDate: null,
  endDate: null,
  monthlyPrice: 0,
  autoRenew: true,
  membershipNumber: '',
  memberGroupId: undefined,
  groupRole: undefined, // No default group role - only set when joining a group
  notes: '',
  selectedFees: [],
  selectedDiscounts: []
};

// Define status options for select dropdown
const statusOptions = [
  { value: 'active', label: 'Active' },
  { value: 'expired', label: 'Expired' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'suspended', label: 'Suspended' },
  { value: 'on_hold', label: 'On Hold' },
  { value: 'pending', label: 'Pending' }
];

const groupRoleOptions = [
  { value: 'primary', label: 'Primary' },
  { value: 'secondary', label: 'Secondary' },
  { value: 'dependent', label: 'Dependent' }
];

const MemberManagementPage: React.FC = () => {
  const { user: currentUser } = useAuth();
  const location = useLocation();

  const [memberProfiles, setMemberProfiles] = useState<MemberProfile[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Filter states
  const [searchText, setSearchText] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [membershipTierFilter, setMembershipTierFilter] = useState<string>('');
  const [onboardingCompleteFilter, setOnboardingCompleteFilter] = useState<string>('');

  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [formData, setFormData] = useState<MemberProfileFormData>(initialFormData);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const [membershipTiers, setMembershipTiers] = useState<MembershipTier[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loadingMemberships, setLoadingMemberships] = useState<boolean>(false);
  const [loadingMembers, setLoadingMembers] = useState<boolean>(false);

  // Fees and discounts state
  const [oneTimeFees, setOneTimeFees] = useState<OneTimeFee[]>([]);
  const [selectedFees, setSelectedFees] = useState<OneTimeFee[]>([]);
  const [discounts, setDiscounts] = useState<Discount[]>([]);
  const [selectedDiscounts, setSelectedDiscounts] = useState<Discount[]>([]);
  
  // Member groups state
  const [memberGroups, setMemberGroups] = useState<MemberGroup[]>([]);
  const [loadingGroups, setLoadingGroups] = useState<boolean>(false);
  
  // Dialogs
  const [feeDialogOpen, setFeeDialogOpen] = useState(false);
  const [discountDialogOpen, setDiscountDialogOpen] = useState(false);

  // Fetch all member profiles
  const fetchMemberProfiles = async () => {
    setLoading(true);
    try {
      const response = await memberProfileAPI.getAllMemberProfiles();
      setMemberProfiles(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching member profiles:', err);
      setError('Failed to load member profiles. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Fetch membership tiers
  const fetchMembershipTiers = async () => {
    setLoadingMemberships(true);
    try {
      const response = await membershipTierAPI.getMembershipTiers();
      setMembershipTiers(response.data);
    } catch (err) {
      console.error('Error fetching membership tiers:', err);
      setError('Failed to load membership options. Please refresh the page.');
    } finally {
      setLoadingMemberships(false);
    }
  };

  // Fetch users for the dropdown
  const fetchUsers = async () => {
    setLoadingMembers(true);
    try {
      const response = await userAPI.getUsers();
      setUsers(response.data);
    } catch (err) {
      console.error('Error fetching users:', err);
    } finally {
      setLoadingMembers(false);
    }
  };

  // Fetch one-time fees
  const fetchOneTimeFees = async () => {
    try {
      const response = await oneTimeFeeAPI.getAll();
      setOneTimeFees(response.data || []);
    } catch (err) {
      console.error('Error fetching one-time fees:', err);
    }
  };

  // Fetch discounts
  const fetchDiscounts = async () => {
    try {
      const response = await discountAPI.getAll();
      setDiscounts(response.data || []);
    } catch (err) {
      console.error('Error fetching discounts:', err);
    }
  };

  // Fetch member groups
  const fetchMemberGroups = async () => {
    setLoadingGroups(true);
    try {
      const response = await memberGroupAPI.getAll();
      setMemberGroups(response.data || []);
    } catch (err) {
      console.error('Error fetching member groups:', err);
    } finally {
      setLoadingGroups(false);
    }
  };

  useEffect(() => {
    fetchMemberProfiles();
    fetchMembershipTiers();
    fetchUsers();
    fetchOneTimeFees();
    fetchDiscounts();
    fetchMemberGroups();
  }, []);

  // Handle filters from navigation state (e.g., from Management Report)
  useEffect(() => {
    const state = location.state as any;
    if (state?.filterTier && membershipTiers.length > 0) {
      // Find the tier ID by name
      const tier = membershipTiers.find(t => t.name === state.filterTier);
      if (tier) {
        setMembershipTierFilter(tier.id);
      }
    }
    if (state?.filterStatus) {
      setStatusFilter(state.filterStatus);
    }
    if (state?.filterOnboardingComplete !== undefined) {
      setOnboardingCompleteFilter(state.filterOnboardingComplete);
    }
  }, [location.state, membershipTiers]);

  // Handle form field changes
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });

    // Clear error for this field if it exists
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: ''
      });
    }
  };

  // Handle select changes
  const handleSelectChange = (e: SelectChangeEvent<string>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  // Handle date changes
  const handleDateOfBirthChange = (date: Date | null) => {
    setFormData({
      ...formData,
      dateOfBirth: date
    });
  };

  const handleStartDateChange = (date: Date | null) => {
    setFormData({
      ...formData,
      startDate: date
    });
  };

  const handleEndDateChange = (date: Date | null) => {
    setFormData({
      ...formData,
      endDate: date
    });
  };

  // Handle member group selection
  const handleMemberGroupChange = (event: any, newValue: MemberGroup | null) => {
    setFormData({
      ...formData,
      memberGroupId: newValue?.id || undefined,
      groupRole: newValue ? 'secondary' : undefined // Only set role when group is selected, clear when no group
    });
  };

  // Fee and discount handlers
  const handleFeeDialogToggle = () => {
    setFeeDialogOpen(!feeDialogOpen);
  };

  const handleDiscountDialogToggle = () => {
    setDiscountDialogOpen(!discountDialogOpen);
  };

  const handleAddFee = (fee: OneTimeFee) => {
    setSelectedFees([...selectedFees, fee]);
    setFeeDialogOpen(false);
  };

  const handleAddDiscount = (discount: Discount) => {
    setSelectedDiscounts([...selectedDiscounts, discount]);
    setDiscountDialogOpen(false);
  };

  const handleRemoveFee = (feeId: number) => {
    setSelectedFees(selectedFees.filter(fee => fee.id !== feeId));
  };

  const handleRemoveDiscount = (discountId: string) => {
    setSelectedDiscounts(selectedDiscounts.filter(discount => discount.id !== discountId));
  };

  // Format currency
  const formatCurrency = (amount: string | number | undefined) => {
    if (amount === undefined) return '$0.00';
    const numericAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(numericAmount);
  };

  // Open dialog for creating a new member profile
  const handleOpenCreateDialog = () => {
    setFormData(initialFormData);
    setFormErrors({});
    setSelectedFees([]);
    setSelectedDiscounts([]);
    setDialogOpen(true);
  };

  // Close dialog
  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  // Validate form
  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.userId) errors.userId = 'User selection is required';
    if (!formData.firstName) errors.firstName = 'First name is required';
    if (!formData.lastName) errors.lastName = 'Last name is required';

    if (!formData.email) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Email is invalid';
    }

    if (!formData.membershipTierId) errors.membershipTierId = 'Membership tier is required';

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle submit for creating a member profile
  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      // Prepare member profile data for API
      const memberProfileData: CreateMemberProfileRequest = {
        userId: formData.userId,
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        mobile: formData.mobile,
        address: formData.address,
        city: formData.city,
        state: formData.state,
        zipCode: formData.zipCode,
        country: formData.country,
        dateOfBirth: formData.dateOfBirth ? format(formData.dateOfBirth, 'yyyy-MM-dd') : undefined,
        membershipTierId: formData.membershipTierId,
        startDate: formData.startDate ? format(formData.startDate, 'yyyy-MM-dd') : undefined,
        endDate: formData.endDate ? format(formData.endDate, 'yyyy-MM-dd') : undefined,
        monthlyPrice: formData.monthlyPrice,
        autoRenew: formData.autoRenew,
        membershipNumber: formData.membershipNumber,
        memberGroupId: formData.memberGroupId,
        groupRole: formData.memberGroupId ? formData.groupRole : undefined, // Only include group role if member is in a group
        notes: formData.notes,
        selectedFees: selectedFees.map(fee => fee.id.toString()),
        selectedDiscounts: selectedDiscounts.map(discount => discount.id)
      };

      // Create member profile
      await memberProfileAPI.createMemberProfile(memberProfileData);
      setSuccess('Member profile created successfully!');

      // Close dialog and refresh data
      setDialogOpen(false);
      fetchMemberProfiles();

      // Clear success message after 5 seconds
      setTimeout(() => {
        setSuccess(null);
      }, 5000);
    } catch (err: any) {
      console.error('Error creating member profile:', err);
      setError(err.response?.data?.message || 'Failed to create member profile. Please try again.');
    }
  };

  // Get status color based on membership status
  const getStatusColor = (status: MembershipStatus) => {
    switch (status) {
      case 'active': return 'success';
      case 'pending': return 'warning';
      case 'expired': return 'warning';
      case 'cancelled': return 'error';
      case 'suspended': return 'error';
      case 'on_hold': return 'info';
      default: return 'default';
    }
  };

  // Get group role color
  const getGroupRoleColor = (role: GroupRole) => {
    switch (role) {
      case 'primary': return 'primary';
      case 'secondary': return 'secondary';
      case 'dependent': return 'info';
      default: return 'default';
    }
  };

  // Filter member profiles based on search and filter criteria
  const filteredMemberProfiles = useMemo(() => {
    let filtered = [...memberProfiles];

    // Text search (name and email)
    if (searchText) {
      const searchLower = searchText.toLowerCase();
      filtered = filtered.filter(profile =>
        `${profile.firstName} ${profile.lastName}`.toLowerCase().includes(searchLower) ||
        profile.email.toLowerCase().includes(searchLower) ||
        profile.membershipNumber?.toLowerCase().includes(searchLower)
      );
    }

    // Status filter
    if (statusFilter) {
      filtered = filtered.filter(profile => profile.status === statusFilter);
    }

    // Membership tier filter
    if (membershipTierFilter) {
      filtered = filtered.filter(profile => profile.membershipTierId === membershipTierFilter);
    }

    // Onboarding complete filter
    if (onboardingCompleteFilter) {
      if (onboardingCompleteFilter === 'complete') {
        filtered = filtered.filter(profile => 
          profile.onboardingStatus?.billingSetupCompleted && 
          profile.onboardingStatus?.documentReviewCompleted
        );
      } else if (onboardingCompleteFilter === 'incomplete') {
        filtered = filtered.filter(profile => 
          !profile.onboardingStatus?.billingSetupCompleted || 
          !profile.onboardingStatus?.documentReviewCompleted
        );
      }
    }

    return filtered;
  }, [memberProfiles, searchText, statusFilter, membershipTierFilter, onboardingCompleteFilter]);

  // Clear all filters
  const clearFilters = () => {
    setSearchText('');
    setStatusFilter('');
    setMembershipTierFilter('');
    setOnboardingCompleteFilter('');
  };

  // Get unique membership tiers from profiles
  const getUniqueMembershipTiers = () => {
    const tierIds = memberProfiles.map(profile => profile.membershipTierId);
    const uniqueTierIds = [...new Set(tierIds)];
    return membershipTiers.filter(tier => uniqueTierIds.includes(tier.id));
  };

  // Define columns for DataGrid
  const columns: GridColDef[] = [
    {
      field: 'firstName',
      headerName: 'Name',
      width: 200,
      renderCell: (params: GridRenderCellParams) => {
        const { firstName, lastName } = params.row;
        const fullName = `${firstName || ''} ${lastName || ''}`.trim();
        return (
          <RouterLink
            to={`/member-profiles/${params.row.id}`}
            style={{ 
              textDecoration: 'none',
              color: 'primary.main',
            }}
          >
            {fullName}
          </RouterLink>
        );
      },
    },
    {
      field: 'email',
      headerName: 'Email',
      width: 250,
    },
    {
      field: 'membershipTier',
      headerName: 'Membership Tier',
      width: 180,
      renderCell: (params: GridRenderCellParams) => (
        params.row.membershipTier?.name || 'Unknown'
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={params.value?.replace('_', ' ') || ''}
          size="small"
          color={getStatusColor(params.value) as any}
        />
      ),
    },
    {
      field: 'startDate',
      headerName: 'Start Date',
      width: 150,
      renderCell: (params: GridRenderCellParams) => {
        if (!params.value) return 'N/A';
        return formatDate(params.value);
      },
    },
    {
      field: 'monthlyPrice',
      headerName: 'Monthly Price',
      width: 130,
      renderCell: (params: GridRenderCellParams) => {
        const price = params.value;
        return price ? `$${Number(price).toFixed(2)}` : 'N/A';
      },
    },
  ];

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <AdminAccessControl allowedRoles={[UserRole.ADMIN, UserRole.COACH, UserRole.SUPER_ADMIN, UserRole.EMPLOYEE]}>
      {/* Breadcrumbs */}
      <Breadcrumbs
        separator={<NavigateNextIcon fontSize="small" />}
        aria-label="breadcrumb"
        sx={{ mb: 3 }}
      >
        <RouterLink
          className='member-link'
          style={{ display: 'flex', alignItems: 'center' }}
          to="/dashboard"
        >
          <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
          Dashboard
        </RouterLink>
        <Typography color="text.primary">Member Management</Typography>
      </Breadcrumbs>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Member Management
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleOpenCreateDialog}
        >
          Create Member Profile
        </Button>
      </Box>

      {/* Success message */}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Error message */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Main content */}
      <Paper sx={{ p: 3 }}>
        {/* Filters */}
        <Stack spacing={2} sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField
              placeholder="Search by name, email, or membership number..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              size="small"
              sx={{ minWidth: 300 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />

            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="Status"
              >
                <MenuItem value="">All</MenuItem>
                {statusOptions.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Membership Tier</InputLabel>
              <Select
                value={membershipTierFilter}
                onChange={(e) => setMembershipTierFilter(e.target.value)}
                label="Membership Tier"
              >
                <MenuItem value="">All</MenuItem>
                {getUniqueMembershipTiers().map(tier => (
                  <MenuItem key={tier.id} value={tier.id}>
                    {tier.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Onboarding</InputLabel>
              <Select
                value={onboardingCompleteFilter}
                onChange={(e) => setOnboardingCompleteFilter(e.target.value)}
                label="Onboarding"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="complete">Complete</MenuItem>
                <MenuItem value="incomplete">Incomplete</MenuItem>
              </Select>
            </FormControl>

            <Button
              variant="outlined"
              onClick={clearFilters}
              startIcon={<ClearIcon />}
              size="small"
            >
              Clear Filters
            </Button>
          </Box>
        </Stack>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : memberProfiles.length === 0 ? (
          <Box sx={{ textAlign: 'center', p: 4 }}>
            <Typography variant="body1">
              No member profiles found. Click "Create Member Profile" to add one.
            </Typography>
          </Box>
        ) : filteredMemberProfiles.length === 0 ? (
          <Box sx={{ textAlign: 'center', p: 4 }}>
            <Typography variant="body1">
              No member profiles match the current filters. Try adjusting your search criteria.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ height: 700, width: '100%' }}>
            <DataGrid
              rows={filteredMemberProfiles}
              columns={columns}
              getRowId={(row) => row.id}
              initialState={{
                pagination: {
                  paginationModel: { page: 0, pageSize: 10 },
                },
                sorting: {
                  sortModel: [{ field: 'firstName', sort: 'asc' }],
                },
              }}
              pageSizeOptions={[5, 10, 25, 50]}
              checkboxSelection={false}
              disableRowSelectionOnClick
              loading={loading}
            />
          </Box>
        )}
      </Paper>

      {/* Create Member Profile Dialog */}
      <Dialog
        open={dialogOpen}
        disableEscapeKeyDown={true}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Create New Member Profile</DialogTitle>
        <DialogContent>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid size={{ xs: 12, sm: 6 }}>
                <Autocomplete
                  options={users}
                  getOptionLabel={(option) => `${option.firstName} ${option.lastName} (${option.email})`}
                  value={users.find(user => user.id === formData.userId) || null}
                  onChange={(event, newValue) => {
                    handleSelectChange({
                      target: { name: 'userId', value: newValue?.id || '' }
                    } as SelectChangeEvent<string>);
                  }}
                  loading={loadingMembers}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select User"
                      required
                      error={!!formErrors.userId}
                      helperText={formErrors.userId}
                      placeholder="Search for a user..."
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingMembers ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  filterOptions={(options, { inputValue }) => {
                    const filtered = options.filter(option =>
                      option.firstName.toLowerCase().includes(inputValue.toLowerCase()) ||
                      option.lastName.toLowerCase().includes(inputValue.toLowerCase()) ||
                      option.email.toLowerCase().includes(inputValue.toLowerCase())
                    );
                    return filtered.sort((a, b) => {
                      const firstNameCompare = a.firstName.localeCompare(b.firstName);
                      if (firstNameCompare !== 0) return firstNameCompare;
                      return a.lastName.localeCompare(b.lastName);
                    });
                  }}
                  noOptionsText="No users found"
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  name="firstName"
                  label="First Name"
                  value={formData.firstName}
                  onChange={handleChange}
                  fullWidth
                  required
                  error={!!formErrors.firstName}
                  helperText={formErrors.firstName}
                />
              </Grid>
               <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  name="lastName"
                  label="Last Name"
                  value={formData.lastName}
                  onChange={handleChange}
                  fullWidth
                  required
                  error={!!formErrors.lastName}
                  helperText={formErrors.lastName}
                />
              </Grid>

               <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  name="email"
                  label="Email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  fullWidth
                  required
                  error={!!formErrors.email}
                  helperText={formErrors.email}
                />
              </Grid>
               <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  name="phone"
                  label="Phone"
                  value={formData.phone}
                  onChange={handleChange}
                  fullWidth
                />
              </Grid>

             <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  name="mobile"
                  label="Mobile"
                  value={formData.mobile}
                  onChange={handleChange}
                  fullWidth
                />
              </Grid>
             <Grid size={{ xs: 12, sm: 6 }}>
                <DatePicker
                  label="Date of Birth"
                  value={formData.dateOfBirth}
                  onChange={handleDateOfBirthChange}
                  slotProps={{
                    textField: { fullWidth: true }
                  }}
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  name="address"
                  label="Address"
                  value={formData.address}
                  onChange={handleChange}
                  fullWidth
                  multiline
                  rows={2}
                />
              </Grid>

            <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  name="city"
                  label="City"
                  value={formData.city}
                  onChange={handleChange}
                  fullWidth
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  name="state"
                  label="State/Province"
                  value={formData.state}
                  onChange={handleChange}
                  fullWidth
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  name="zipCode"
                  label="Zip/Postal Code"
                  value={formData.zipCode}
                  onChange={handleChange}
                  fullWidth
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  name="country"
                  label="Country"
                  value={formData.country}
                  onChange={handleChange}
                  fullWidth
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth required error={!!formErrors.membershipTierId}>
                  <InputLabel>Membership Tier</InputLabel>
                  <Select
                    name="membershipTierId"
                    value={formData.membershipTierId}
                    onChange={handleSelectChange}
                    label="Membership Tier"
                    disabled={loadingMemberships}
                  >
                    {membershipTiers.map(tier => (
                      <MenuItem key={tier.id} value={tier.id}>
                        {tier.name} - ${tier.monthlyPrice}/month
                      </MenuItem>
                    ))}
                  </Select>
                  {formErrors.membershipTierId && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                      {formErrors.membershipTierId}
                    </Typography>
                  )}
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <DatePicker
                  label="Start Date"
                  value={formData.startDate}
                  onChange={handleStartDateChange}
                  slotProps={{
                    textField: { fullWidth: true }
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <DatePicker
                  label="End Date"
                  value={formData.endDate}
                  onChange={handleEndDateChange}
                  slotProps={{
                    textField: { fullWidth: true }
                  }}
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  name="monthlyPrice"
                  label="Monthly Price"
                  type="number"
                  value={formData.monthlyPrice}
                  onChange={handleChange}
                  fullWidth
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Autocomplete
                  options={memberGroups}
                  getOptionLabel={(option) => `${option.groupName} (${option.groupType})`}
                  value={memberGroups.find(group => group.id === formData.memberGroupId) || null}
                  onChange={handleMemberGroupChange}
                  loading={loadingGroups}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Member Group (Optional)"
                      placeholder="Search for a group..."
                      helperText="Assign to an existing group as a secondary member"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingGroups ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <Box component="li" {...props}>
                      <Box>
                        <Typography variant="body1">
                          {option.groupName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {option.groupType} • {option.maxMembers ? `Max ${option.maxMembers} members` : 'No limit'} • {option.groupDiscountPercent}% discount
                        </Typography>
                      </Box>
                    </Box>
                  )}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  filterOptions={(options, { inputValue }) => {
                    return options.filter(option =>
                      option.groupName.toLowerCase().includes(inputValue.toLowerCase()) ||
                      option.groupType.toLowerCase().includes(inputValue.toLowerCase()) ||
                      (option.companyName && option.companyName.toLowerCase().includes(inputValue.toLowerCase()))
                    );
                  }}
                  noOptionsText="No groups found"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth>
                  <InputLabel>Group Role</InputLabel>
                  <Select
                    name="groupRole"
                    value={formData.groupRole || ''}
                    onChange={handleSelectChange}
                    label="Group Role"
                    disabled={!formData.memberGroupId} // Disable when no group is selected
                  >
                    {groupRoleOptions.map(option => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {formData.memberGroupId ? (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                      Role within the selected group
                    </Typography>
                  ) : (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                      Select a group first to assign a role
                    </Typography>
                  )}
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <TextField
                  name="membershipNumber"
                  label="Membership Number"
                  value={formData.membershipNumber}
                  onChange={handleChange}
                  fullWidth
                />
              </Grid>

              {/* One-Time Fees Section */}
              <Grid size={{ xs: 12 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">
                    One-Time Fees
                  </Typography>
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={handleFeeDialogToggle}
                    size="small"
                  >
                    Add Fee
                  </Button>
                </Box>
                {selectedFees.length > 0 ? (
                  <List dense>
                    {selectedFees.map((fee) => (
                      <ListItem
                        key={fee.id}
                        secondaryAction={
                          <IconButton edge="end" aria-label="delete" onClick={() => handleRemoveFee(fee.id)}>
                            <DeleteIcon />
                          </IconButton>
                        }
                      >
                        <ListItemText
                          primary={fee.feeName}
                          secondary={formatCurrency(fee.amount)}
                        />
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography variant="body2" color="textSecondary">
                    No one-time fees added
                  </Typography>
                )}
              </Grid>

              {/* Discounts Section */}
              <Grid size={{ xs: 12 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">
                    Discounts
                  </Typography>
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={handleDiscountDialogToggle}
                    size="small"
                  >
                    Add Discount
                  </Button>
                </Box>
                {selectedDiscounts.length > 0 ? (
                  <List dense>
                    {selectedDiscounts.map((discount) => (
                      <ListItem
                        key={discount.id}
                        secondaryAction={
                          <IconButton edge="end" aria-label="delete" onClick={() => handleRemoveDiscount(discount.id)}>
                            <DeleteIcon />
                          </IconButton>
                        }
                      >
                        <ListItemText
                          primary={discount.name}
                          secondary={discount.category === 'percentage' ?
                            `${discount.value}%` :
                            formatCurrency(discount.value)}
                        />
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography variant="body2" color="textSecondary">
                    No discounts added
                  </Typography>
                )}
              </Grid>

              <Grid size={{ xs: 12 }}>
                <TextField
                  name="notes"
                  label="Notes"
                  value={formData.notes}
                  onChange={handleChange}
                  fullWidth
                  multiline
                  rows={3}
                />
              </Grid>
            </Grid>
          </LocalizationProvider>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            Create Member Profile
          </Button>
        </DialogActions>
      </Dialog>

      {/* Fee Dialog */}
      <Dialog open={feeDialogOpen} disableEscapeKeyDown={true} maxWidth="sm" fullWidth>
        <DialogTitle>Add One-Time Fee</DialogTitle>
        <DialogContent>
          <List>
            {oneTimeFees.filter(fee => !selectedFees.some(selected => selected.id === fee.id)).map((fee) => (
              <ListItem
                key={fee.id}
                onClick={() => handleAddFee(fee)}
                sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' } }}
              >
                <ListItemText
                  primary={fee.feeName}
                  secondary={`${fee.feeType} - ${formatCurrency(fee.amount)}`}
                />
              </ListItem>
            ))}
          </List>
          {oneTimeFees.filter(fee => !selectedFees.some(selected => selected.id === fee.id)).length === 0 && (
            <Typography variant="body2" color="textSecondary" sx={{ p: 2, textAlign: 'center' }}>
              No additional fees available
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleFeeDialogToggle} color="primary">
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Discount Dialog */}
      <Dialog open={discountDialogOpen} disableEscapeKeyDown={true} maxWidth="sm" fullWidth>
        <DialogTitle>Add Discount</DialogTitle>
        <DialogContent>
          <List>
            {discounts.filter(discount => !selectedDiscounts.some(selected => selected.id === discount.id)).map((discount) => (
              <ListItem
                key={discount.id}
                onClick={() => handleAddDiscount(discount)}
                sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' } }}
              >
                <ListItemText
                  primary={discount.name}
                  secondary={discount.category === 'percentage' ?
                    `${discount.value}% (${discount.type})` :
                    `${formatCurrency(discount.value)} (${discount.type})`}
                />
              </ListItem>
            ))}
          </List>
          {discounts.filter(discount => !selectedDiscounts.some(selected => selected.id === discount.id)).length === 0 && (
            <Typography variant="body2" color="textSecondary" sx={{ p: 2, textAlign: 'center' }}>
              No additional discounts available
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDiscountDialogToggle} color="primary">
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
      </AdminAccessControl>
    </Container>
  );
};

export default MemberManagementPage;

/*
In your App.css, add or update:
.member-link {
    color: #1976d2;
    cursor: pointer;
    text-decoration: none;
    transition: color 0.2s;
}
.member-link:hover {
    text-decoration: underline;
    color: #115293;
}
*/
